'use client'

/**
 * The files staged on a chat composer, waiting for the next question (#16101).
 *
 * ONE implementation for both composers that take files — Home's ask bar
 * (`AskBar` → `AiAskComposer`) and the docked panel's `AiChatInput` — so the
 * two cannot drift: the same checks, the same caps, the same upload, the
 * same chips (`./chat-attachment-chips`).
 *
 * `addFiles(files, via)` is the single entry for the attach button, a drop
 * (page-wide on Home, the panel on the panel) and a paste. Each file goes
 * through `planChatAttachmentStaging` first: an unsupported type, an
 * oversize file and a dropped folder are refused by name in ONE
 * `toastError()`, and files past the per-turn cap are counted in it — nothing
 * refused is uploaded. Each accepted file becomes a chip in the `uploading`
 * state at once, and `ready` when `addAiChatAttachment` has staged it; a
 * file the server refuses (its bytes are not what its name says) leaves with
 * a toast. A HEIC/HEIF photo is converted to JPEG in the browser first
 * (`normalizeImageFile`, #16099), as on every other upload path.
 *
 * Staging sends nothing. `takeForSend()` hands the ready files to a send and
 * clears the chips — the turn in the transcript shows them from then on — and
 * `restore()` puts them back if that send fails, so a file is never silently
 * lost from the composer. A refusal the same files would meet again (a file
 * the route no longer takes) drops them instead, and says so.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  addAiChatAttachment,
  removeAiChatAttachment,
} from '@/app/actions/ai-chat-attachments'
import {
  type ChatAttachmentKind,
  type ChatAttachmentSurface,
  type ChatAttachmentVia,
  type SentChatAttachment,
  CHAT_ATTACHMENT_NOT_FOUND,
  MAX_CHAT_ATTACHMENTS_PER_TURN,
  chatAttachmentKind,
  maxChatAttachmentBytes,
  planChatAttachmentStaging,
  stagedMimeType,
  stagingRejectionMessage,
} from '@/lib/ai-chat/chat-attachments'
import {
  HeicConversionError,
  normalizeImageFile,
} from '@/lib/attachments/convert-heic'
import { isHeicFile } from '@/lib/attachments/heic'
import { safeRandomUUID } from '@/lib/browser-uuid'
import { toastError } from '@/lib/toast'

export type { SentChatAttachment }

export interface ChatAttachmentChip {
  key: string
  name: string
  mimeType: string
  kind: ChatAttachmentKind
  status: 'uploading' | 'ready'
  /** Set once the server has staged it. */
  attachmentId: string | null
  /** A local preview of an image, while it is on the composer. */
  previewUrl: string | null
}

export interface ChatAttachments {
  chips: readonly ChatAttachmentChip[]
  /** True while any chip is still uploading: Send waits for it. */
  isUploading: boolean
  /** At the per-turn cap: the attach button is disabled. */
  atCap: boolean
  /** How many files the call accepted for upload. */
  addFiles: (files: readonly File[], via: ChatAttachmentVia) => number
  remove: (key: string) => void
  /** The ready files for a send, clearing the chips. */
  takeForSend: () => SentChatAttachment[]
  /**
   * Put files a failed send took back on the composer, saying why with the
   * route's `reason` when it gave one.
   */
  restore: (files: readonly SentChatAttachment[], reason?: string) => void
}

function previewFor(file: File): string | null {
  // A HEIC is not drawable in most browsers; its chip shows the image icon.
  if (!file.type.startsWith('image/') || isHeicFile(file)) return null
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null
  }
  return URL.createObjectURL(file)
}

/** `text` ending in a full stop, for a toast that continues after it. */
function sentence(text: string): string {
  return /[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`
}

function revoke(chip: ChatAttachmentChip) {
  if (chip.previewUrl && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(chip.previewUrl)
  }
}

export function useChatAttachments({
  surface,
}: {
  surface: ChatAttachmentSurface
}): ChatAttachments {
  const [chips, setChips] = useState<ChatAttachmentChip[]>([])
  // Mirrors `chips` synchronously, so two drops in one tick both see the cap.
  const chipsRef = useRef<ChatAttachmentChip[]>([])
  const update = useCallback(
    (next: (current: ChatAttachmentChip[]) => ChatAttachmentChip[]) => {
      chipsRef.current = next(chipsRef.current)
      setChips(chipsRef.current)
    },
    [],
  )

  useEffect(
    () => () => {
      for (const chip of chipsRef.current) revoke(chip)
    },
    [],
  )

  const upload = useCallback(
    async (file: File, key: string, via: ChatAttachmentVia) => {
      let error = 'Failed to attach the file'
      try {
        // Only a HEIC waits on the converter; any other file uploads as is.
        const converted = isHeicFile(file)
          ? await normalizeImageFile(file, {
              maxSizeBytes: maxChatAttachmentBytes('image/jpeg'),
            })
          : file
        const data = new FormData()
        data.set('file', converted)
        data.set('via', via)
        data.set('surface', surface)
        const result = await addAiChatAttachment(data)
        if (result.success) {
          const staged = chipsRef.current.some(chip => chip.key === key)
          if (!staged) {
            // Removed while it uploaded: take the staged row back too, or it
            // would hold a place under the staging cap with no chip to
            // remove it from. Best effort, as in `remove`.
            void Promise.resolve(
              removeAiChatAttachment(result.data.attachmentId),
            ).catch(() => {})
            return
          }
          update(current =>
            current.map(chip =>
              chip.key === key
                ? {
                    ...chip,
                    status: 'ready',
                    attachmentId: result.data.attachmentId,
                    mimeType: result.data.mimeType,
                    kind: result.data.kind,
                  }
                : chip,
            ),
          )
          return
        }
        error = result.error
      } catch (caught) {
        // A HEIC that would not convert says why; any other throw is a
        // transport failure, which the toast below reports and the action
        // logs its own — see sub-issue R carve-out
        if (caught instanceof HeicConversionError) error = caught.message
      }
      const gone = chipsRef.current.find(chip => chip.key === key)
      // Removed while it uploaded: the person already let it go.
      if (!gone) return
      revoke(gone)
      update(current => current.filter(chip => chip.key !== key))
      toastError({
        message: `${file.name}: ${error}`,
        context: { action: 'addAiChatAttachment', surface },
      })
    },
    [surface, update],
  )

  const addFiles = useCallback(
    (files: readonly File[], via: ChatAttachmentVia): number => {
      const plan = planChatAttachmentStaging(files, chipsRef.current.length)
      const refusal = stagingRejectionMessage(plan)
      if (refusal) {
        toastError({
          message: refusal,
          context: { action: 'stageChatAttachments', surface },
        })
      }
      const added = plan.accepted.map(file => {
        const mimeType = stagedMimeType(file) ?? file.type
        return {
          file,
          chip: {
            key: safeRandomUUID(),
            name: file.name,
            mimeType,
            kind: chatAttachmentKind(mimeType),
            status: 'uploading' as const,
            attachmentId: null,
            previewUrl: previewFor(file),
          },
        }
      })
      if (added.length === 0) return 0
      update(current => [...current, ...added.map(entry => entry.chip)])
      for (const { file, chip } of added) void upload(file, chip.key, via)
      return added.length
    },
    [surface, update, upload],
  )

  const remove = useCallback(
    (key: string) => {
      const chip = chipsRef.current.find(entry => entry.key === key)
      if (!chip) return
      revoke(chip)
      update(current => current.filter(entry => entry.key !== key))
      // Staged on the server: take it back too. Best effort — a staged file
      // left behind is the uploader's alone and ages out of the window.
      if (chip.attachmentId) {
        void Promise.resolve(removeAiChatAttachment(chip.attachmentId)).catch(
          () => {},
        )
      }
    },
    [update],
  )

  const takeForSend = useCallback((): SentChatAttachment[] => {
    const ready = chipsRef.current.filter(
      (chip): chip is ChatAttachmentChip & { attachmentId: string } =>
        chip.status === 'ready' && chip.attachmentId !== null,
    )
    const taken = new Set(ready.map(chip => chip.key))
    for (const chip of ready) revoke(chip)
    update(current => current.filter(chip => !taken.has(chip.key)))
    return ready.map(chip => ({
      id: chip.attachmentId,
      fileName: chip.name,
      mimeType: chip.mimeType,
    }))
  }, [update])

  const restore = useCallback(
    (files: readonly SentChatAttachment[], reason?: string) => {
      if (files.length === 0) return
      if (reason === CHAT_ATTACHMENT_NOT_FOUND) {
        // Every retry with these ids would be refused the same way (a file
        // staged too long ago, or already sent), so they are not put back.
        toastError({
          message:
            'Your question was not sent: its files can no longer be sent. Attach them again.',
          context: { action: 'sendChatAttachments', surface },
        })
        return
      }
      update(current => [
        ...files.map(file => ({
          key: safeRandomUUID(),
          name: file.fileName,
          mimeType: file.mimeType,
          kind: chatAttachmentKind(file.mimeType),
          status: 'ready' as const,
          attachmentId: file.id,
          previewUrl: null,
        })),
        ...current,
      ])
      toastError({
        message: reason
          ? `Your question was not sent: ${sentence(reason)} Your files are still attached.`
          : 'Your question was not sent. Your files are still attached.',
        context: { action: 'sendChatAttachments', surface },
      })
    },
    [surface, update],
  )

  return useMemo(
    () => ({
      chips,
      isUploading: chips.some(chip => chip.status === 'uploading'),
      atCap: chips.length >= MAX_CHAT_ATTACHMENTS_PER_TURN,
      addFiles,
      remove,
      takeForSend,
      restore,
    }),
    [chips, addFiles, remove, takeForSend, restore],
  )
}
